<template>
    <ActivityDetailsCard activityType="Insider Transactions" :title="item.description" :entity="item.entity">
        <template #sub-header>
            <div class="cell">
                <h5>Date:</h5>
                <label>{{ formatDate(item.details.date) }}</label>
            </div>
            <div class="cell">
                <h5>Action:</h5>
                <label>{{ item.details.action }}</label>
            </div>
            <div class="cell">
                <h5>Shares:</h5>
                <label>{{ formatNumber(item.details.shares) }}</label>
            </div>
            <div class="cell" v-if="hasPriceRange">
                <h5>Price Range:</h5>
                <label>{{ formatCurrency(item.details.priceMin) }} - {{ formatCurrency(item.details.priceMax) }}</label>
            </div>
            <div class="cell" v-else>
                <h5>Price:</h5>
                <label>{{ formatCurrency(price) }}</label>
            </div>
            <div class="cell">
                <h5>Value:</h5>
                <label>{{ formatCurrency(item.details.value) }}</label>
            </div>
            <div class="cell">
                <h5>Remaining Shares:</h5>
                <label>{{ formatNumber(item.details.remainingShares) }}</label>
            </div>
            <div class="cell">
                <h5>Type:</h5>
                <label>{{ item.details.derivative ? 'Derivative' : 'Non-Derivative' }}</label>
            </div>
        </template>
        <RightSheetSection title="Historical Executive Outlier Performance" v-if="outliers.length > 0">
            <table class="mt-table">
                <thead>
                    <tr>
                        <th data-label="Days" field="returnWindowDays">Days</th>
                        <th
                            data-label="Hit Rate"
                            v-dialog:top="
                                'Number of outlier months that led to alpha divided by number of outlier months'
                            "
                        >
                            Hit Rate
                        </th>
                        <th
                            data-label="Avg. Alpha"
                            v-dialog:top-left="
                                'Average alpha produced following outlier months in 365/180/60 day view period '
                            "
                        >
                            Avg. Alpha
                        </th>
                        <th
                            data-label="Avg. Absolute Return"
                            v-dialog:top-left="
                                'Average absolute return following outlier months in 365/180/60 day view period '
                            "
                        >
                            Avg. Absolute Return
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="outlier in outliers" :key="`outlier-${outlier.returnWindowDays}`">
                        <td data-label="Days">
                            {{ outlier.returnWindowDays }}
                        </td>
                        <td data-label="Hit Rate">
                            {{ formatPercentage(outlier.outlierHitRate) }}
                        </td>
                        <td data-label="Avg. Alpha">
                            {{ formatPercentage(outlier.outlierAlpha) }}
                        </td>
                        <td data-label="Avg. Absolute Return">
                            {{ formatPercentage(outlier.outlierAbsoluteReturn) }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </RightSheetSection>
        <RightSheetSection title="Description">
            <p>{{ item.details.typeExplanation }}</p>
        </RightSheetSection>
        <ActivityStockPriceLine :item="item" :selectedCompany="item.details.companyId" />
        <RightSheetSection title="Transaction History (prior 6 months)">
            <KnobJobs :items="previousTransactions" />
        </RightSheetSection>
        <RightSheetSection title="Details" v-if="item.details.transactions">
            <table class="mt-table">
                <thead>
                    <tr>
                        <th>Shares</th>
                        <th>Price</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(transaction, index) in item.details.transactions" :key="`t-${index}`">
                        <td>
                            {{ formatNumber(transaction.shares) }}
                        </td>
                        <td>
                            {{ formatCurrency(transaction.sharePrice) }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </RightSheetSection>
    </ActivityDetailsCard>
</template>
<script>
import { formatNumber, formatCurrency } from '@/utils/format'

import ActivityDetailsCard from './ActivityDetailsCard.vue'
import ActivityStockPriceLine from './ActivityStockPriceLine.vue'
import KnobJobs from '@/components/activity/ActivityKnobJobs.vue'
import RightSheetSection from '@/components/shared/RightSheetSection.vue'
import { unpack as unpackOutlierInsiderTransactions } from '@/services/outlierInsiderTransactionService.js'

export default {
    components: {
        ActivityDetailsCard,
        ActivityStockPriceLine,
        KnobJobs,
        RightSheetSection,
    },
    props: {
        item: {
            type: Object,
            required: true,
        },
    },
    computed: {
        outliers() {
            const outliers = this.item?.details?.outliers

            if (!outliers) {
                return []
            }

            return unpackOutlierInsiderTransactions(outliers).sort(
                (lhs, rhs) => lhs.returnWindowDays - rhs.returnWindowDays
            )
        },
        previousTransactions() {
            return this.item?.details?.previousTransactions || []
        },
        hasPriceRange() {
            return this.item?.details?.priceMin != this.item?.details?.priceMax
        },
        price() {
            return this.item?.details?.priceMin ?? this.item?.details?.price
        },
    },
    methods: {
        coalesce(item) {
            if (!item) {
                return 'N/A'
            }
            return item
        },
        formatDate(date) {
            if (date) {
                return new Date(date).toLocaleDateString()
            }
            return 'N/A'
        },
        formatCurrency(value) {
            if (value != undefined && value != null) {
                return formatCurrency(value)
            }
            return 'N/A'
        },
        formatNumber(value) {
            if (value != undefined && value != null) {
                return formatNumber(value)
            }
            return 'N/A'
        },
        formatPercentage(value) {
            value = parseFloat(value) * 100
            if (!isNaN(value)) {
                return value < 0 ? `(${(-value).toFixed(1)}%)` : `${value.toFixed(1)}%`
            }
            return '-'
        },
    },
}
</script>
